/**
 * LabReportsPage Component
 * Displays test results organized by brand -> product -> batch
 * With sidebar navigation for brands
 * Products are loaded on-demand when a brand is expanded
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, InputGroup, Collapse } from 'react-bootstrap';
import { useLabReports, useLabReportProducts } from '../../hooks/useProducts.js';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { imageHelpers } from '../../services/api.js';

// Component to render a brand's products (loaded on-demand)
const BrandProducts = ({ brandId, isExpanded, expandedProducts, toggleProduct }) => {
  const { products, isLoading, error } = useLabReportProducts(brandId, {
    enabled: isExpanded // Only fetch when brand is expanded
  });

  if (!isExpanded) return null;

  if (isLoading) {
    return (
      <div className="border-top p-3 text-center">
        <div className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="visually-hidden">Loading products...</span>
        </div>
        <span className="ms-2 text-muted">Loading products...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-top p-3 text-center text-danger">
        Error loading products: {error.message}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="border-top p-3 text-center text-muted">
        No products with COAs found
      </div>
    );
  }

  return (
    <div className="border-top ps-5">
      {products.map((product) => (
        <div key={product.id} className="product-section">
          {/* Product Header */}
          <div
            className="product-header px-3 py-2 d-flex justify-content-between align-items-center cursor-pointer border-bottom"
            onClick={() => toggleProduct(product.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleProduct(product.id)}
            style={{ paddingLeft: '2.5rem' }}
          >
            <div className="d-flex align-items-center">
              <i className={`bi bi-chevron-${expandedProducts[product.id] ? 'down' : 'right'} me-2`}></i>
              <div>
                <h6 className="mb-0">
                  {product.name}
                  {product.flavor && (
                    <span className="text-muted ms-2">- {product.flavor}</span>
                  )}
                </h6>
              </div>
            </div>
            <span className="badge bg-primary rounded-pill">
              {product.batchCount} {product.batchCount === 1 ? 'batch' : 'batches'}
            </span>
          </div>

          {/* Batches - Collapsible */}
          <Collapse in={expandedProducts[product.id]}>
            <div className="ps-5">
              {product.batches.map((batch) => (
                <div
                  key={batch.id}
                  className="batch-item px-3 py-2 d-flex justify-content-between align-items-center border-bottom"
                  style={{ paddingLeft: '4rem', backgroundColor: '#fafafa' }}
                >
                  <div>
                    <span className="fw-medium">{batch.batchName}</span>
                    <small className="text-muted ms-2">
                      ({new Date(batch.dateCreated).toLocaleDateString()})
                    </small>
                  </div>
                  <div className="testing-files">
                    {batch.testingFiles.map((file) => (
                      <a
                        key={file.id}
                        href={file.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                        title={file.displayName}
                      >
                        <i className="bi bi-file-earmark-pdf me-1"></i>
                        View COA
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Collapse>
        </div>
      ))}
    </div>
  );
};

// Component to render preloaded products (from search results)
const SearchResultProducts = ({ products, isExpanded, expandedProducts, toggleProduct }) => {
  if (!isExpanded) return null;

  if (products.length === 0) {
    return (
      <div className="border-top p-3 text-center text-muted">
        No products with COAs found
      </div>
    );
  }

  return (
    <div className="border-top ps-5">
      {products.map((product) => (
        <div key={product.id} className="product-section">
          {/* Product Header */}
          <div
            className="product-header px-3 py-2 d-flex justify-content-between align-items-center cursor-pointer border-bottom"
            onClick={() => toggleProduct(product.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleProduct(product.id)}
            style={{ paddingLeft: '2.5rem' }}
          >
            <div className="d-flex align-items-center">
              <i className={`bi bi-chevron-${expandedProducts[product.id] ? 'down' : 'right'} me-2`}></i>
              <div>
                <h6 className="mb-0">
                  {product.name}
                  {product.flavor && (
                    <span className="text-muted ms-2">- {product.flavor}</span>
                  )}
                </h6>
              </div>
            </div>
            <span className="badge bg-primary rounded-pill">
              {product.batchCount} {product.batchCount === 1 ? 'batch' : 'batches'}
            </span>
          </div>

          {/* Batches - Collapsible */}
          <Collapse in={expandedProducts[product.id]}>
            <div className="ps-5">
              {product.batches.map((batch) => (
                <div
                  key={batch.id}
                  className="batch-item px-3 py-2 d-flex justify-content-between align-items-center border-bottom"
                  style={{ paddingLeft: '4rem', backgroundColor: '#fafafa' }}
                >
                  <div>
                    <span className="fw-medium">{batch.batchName}</span>
                    <small className="text-muted ms-2">
                      ({new Date(batch.dateCreated).toLocaleDateString()})
                    </small>
                  </div>
                  <div className="testing-files">
                    {batch.testingFiles.map((file) => (
                      <a
                        key={file.id}
                        href={file.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                        title={file.displayName}
                      >
                        <i className="bi bi-file-earmark-pdf me-1"></i>
                        View COA
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Collapse>
        </div>
      ))}
    </div>
  );
};

const LabReportsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get URL parameters
  const searchQuery = searchParams.get('search') || '';
  const selectedBrandId = searchParams.get('brand') ? parseInt(searchParams.get('brand')) : null;

  // Local state
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [expandedBrands, setExpandedBrands] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Get lab report brands (lightweight initial load)
  const {
    brands,
    totalProducts,
    totalBatches,
    isLoading,
    error,
    refetch
  } = useLabReports({
    search: debouncedSearch || undefined,
    brandId: selectedBrandId || undefined,
    enabled: true
  });

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (selectedBrandId) params.set('brand', selectedBrandId.toString());
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, selectedBrandId, setSearchParams]);

  // Auto-expand single brand or selected brand
  useEffect(() => {
    if (brands.length === 1) {
      setExpandedBrands({ [brands[0].id]: true });
    } else if (selectedBrandId && brands.find(b => b.id === selectedBrandId)) {
      setExpandedBrands({ [selectedBrandId]: true });
    }
  }, [brands, selectedBrandId]);

  // Handle brand selection from sidebar
  const handleBrandSelect = (brandId) => {
    const params = new URLSearchParams(searchParams);
    if (brandId) {
      params.set('brand', brandId.toString());
    } else {
      params.delete('brand');
    }
    setSearchParams(params);
  };

  // Toggle brand expansion
  const toggleBrand = useCallback((brandId) => {
    setExpandedBrands(prev => ({
      ...prev,
      [brandId]: !prev[brandId]
    }));
  }, []);

  // Toggle product expansion
  const toggleProduct = useCallback((productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  }, []);

  // Expand all brands (products will load on-demand)
  const expandAll = useCallback(() => {
    const allBrands = {};
    brands.forEach(brand => {
      allBrands[brand.id] = true;
    });
    setExpandedBrands(allBrands);
  }, [brands]);

  // Collapse all
  const collapseAll = useCallback(() => {
    setExpandedBrands({});
    setExpandedProducts({});
  }, []);

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setDebouncedSearch('');
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setDebouncedSearch(searchInput);
  };

  return (
    <div className="bg-light min-vh-100">
      {/* Page Header */}
      <section className="bg-white py-4 border-bottom">
        <Container fluid>
          <Row className="align-items-center">
            <Col md={8}>
              {/* Breadcrumb */}
              <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/" className="text-decoration-none">Home</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Lab Reports
                  </li>
                </ol>
              </nav>

              {/* Page Title */}
              <div className="d-flex align-items-center mb-2">
                <div>
                  <h1 className="h2 mb-0">
                    <i className="bi bi-file-earmark-medical me-2"></i>
                    Lab Reports
                  </h1>
                  <p className="text-muted mb-0">
                    {brands.length} brands, {totalProducts} products, {totalBatches} test results
                    {debouncedSearch && ` matching "${debouncedSearch}"`}
                  </p>
                </div>
              </div>
            </Col>

            <Col md={4} className="text-md-end">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={expandAll}
                className="me-2"
              >
                <i className="bi bi-arrows-expand me-1"></i>
                Expand All
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={collapseAll}
              >
                <i className="bi bi-arrows-collapse me-1"></i>
                Collapse All
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Content with Sidebar */}
      <section className="py-4">
        <Container fluid>
          <Row>
            {/* Sidebar - Brand Navigation */}
            <Col md={3} className="mb-4 mb-lg-0">
              <div className="bg-white border rounded p-3" style={{ top: '1rem' }}>
                {/* Brands */}
                <h6 className="text-uppercase text-muted mb-3 small fw-bold">
                  Brands
                </h6>

                {isLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-muted" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : brands.length > 0 ? (
                  <ul className="nav flex-column nav-pills">
                    <li className="nav-item">
                      <button
                        className={`nav-link text-start w-100 ${!selectedBrandId ? 'active' : ''}`}
                        onClick={() => handleBrandSelect(null)}
                      >
                        <span>All Brands</span>
                        <span className="badge bg-secondary float-end">{brands.length}</span>
                      </button>
                    </li>
                    {brands.map(brand => (
                      <li key={brand.id} className="nav-item">
                        <button
                          className={`nav-link text-start w-100 ${selectedBrandId === brand.id ? 'active' : ''}`}
                          onClick={() => handleBrandSelect(brand.id)}
                        >
                          <span className="text-truncate d-block" style={{ maxWidth: '150px' }}>
                            {brand.name}
                          </span>
                          <span className="badge bg-secondary float-end">
                            {brand.batchCount}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted small mb-0">No brands found</p>
                )}
              </div>
            </Col>

            {/* Main Content - Lab Reports */}
            <Col md={9}>
              {/* Search Box */}
              <div className="bg-white border rounded p-3 mb-3">
                <Form onSubmit={handleSearchSubmit}>
                  <InputGroup>
                    <InputGroup.Text>
                      <i className="bi bi-search"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search by product name, flavor, or batch name..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                    {searchInput && (
                      <Button
                        variant="outline-secondary"
                        onClick={handleClearSearch}
                      >
                        <i className="bi bi-x-lg"></i>
                      </Button>
                    )}
                  </InputGroup>
                </Form>
              </div>

              {isLoading ? (
                <div className="text-center py-5">
                  <LoadingSpinner />
                  <p className="mt-3 text-muted">Loading lab reports...</p>
                </div>
              ) : error ? (
                <div className="text-center py-5">
                  <div className="alert alert-warning">
                    <h5>Error Loading Lab Reports</h5>
                    <p>{error.message}</p>
                    <Button variant="primary" onClick={() => refetch()}>
                      Try Again
                    </Button>
                  </div>
                </div>
              ) : brands.length > 0 ? (
                <div className="lab-reports-list">
                  {brands.map((brand) => (
                    <div key={brand.id} className="lab-report-brand bg-white mb-3 border rounded">
                      {/* Brand Header */}
                      <div
                        className="lab-report-header p-3 d-flex justify-content-between align-items-center cursor-pointer bg-light"
                        onClick={() => toggleBrand(brand.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && toggleBrand(brand.id)}
                      >
                        <div className="d-flex align-items-center">
                          <i className={`bi bi-chevron-${expandedBrands[brand.id] ? 'down' : 'right'} me-3 fs-5`}></i>
                          {brand.imageUrl ? (
                            <img
                              src={imageHelpers.getBrandImageUrl(brand)[0]}
                              alt={`${brand.name} logo`}
                              className="brand-logo me-3"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'inline';
                              }}
                            />
                          ) : null}
                          <i
                            className="bi bi-building me-3 fs-4 text-muted"
                            style={{ display: brand.imageUrl ? 'none' : 'inline' }}
                          ></i>
                          <div>
                            <h5 className="mb-0">
                              {brand.name}
                            </h5>
                            <small className="text-muted">
                              {brand.productCount} products, {brand.batchCount} test results
                            </small>
                          </div>
                        </div>
                      </div>

                      {/* Brand Products - Use preloaded products if available (search), otherwise load on demand */}
                      {brand.products && brand.products.length > 0 ? (
                        <SearchResultProducts
                          products={brand.products}
                          isExpanded={expandedBrands[brand.id]}
                          expandedProducts={expandedProducts}
                          toggleProduct={toggleProduct}
                        />
                      ) : (
                        <BrandProducts
                          brandId={brand.id}
                          isExpanded={expandedBrands[brand.id]}
                          expandedProducts={expandedProducts}
                          toggleProduct={toggleProduct}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5 bg-white rounded border">
                  <i className="bi bi-file-earmark-medical display-1 text-muted mb-3"></i>
                  <h3 className="text-muted">No Lab Reports Found</h3>
                  <p className="text-muted">
                    {debouncedSearch
                      ? `No test results match your search for "${debouncedSearch}".`
                      : 'There are no test results available at this time.'
                    }
                  </p>
                  {debouncedSearch && (
                    <Button variant="primary" onClick={handleClearSearch}>
                      Clear Search
                    </Button>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      <style>{`
        .lab-report-header,
        .product-header {
          transition: background-color 0.15s ease-in-out;
        }

        .lab-report-header:hover,
        .product-header:hover {
          background-color: #e9ecef !important;
        }

        .cursor-pointer {
          cursor: pointer;
        }

        .brand-logo {
          max-width: 60px;
          max-height: 40px;
          object-fit: contain;
        }

        .batch-item:last-child {
          border-bottom: none !important;
        }

        .batch-item:hover {
          background-color: #f0f0f0 !important;
        }

        .nav-pills .nav-link {
          color: #495057;
          border-radius: 0;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
        }

        .nav-pills .nav-link:hover {
          background-color: #f8f9fa;
        }

        .nav-pills .nav-link.active {
          background-color: #55A62D;
          color: white;
        }

        .nav-pills .nav-link.active .badge {
          background-color: black !important;
          color: white;
        }
      `}</style>
    </div>
  );
};

export default LabReportsPage;